import './ActivityFeed.css';

import * as React from 'react';
import * as acl from 'common/utils/acl';
import * as addDays from 'date-fns/add_days';
import { Row, Block } from 'emotion-jsxstyle';
import { EntityActions, EntityType } from 'common/entity';

import { Activity } from 'common/api/models/Activity';
import { ActivityApi } from 'common/api/resources/ActivityApi';
import { ActivityFeedCeuForm } from './ActivityFeedCeuForm';
import { ActivityFeedNoteForm } from './ActivityFeedNoteForm';
import { AppState } from 'common/models/AppState';
import { Datetime } from 'common/components/Datetime';
import { InfiniteList } from 'common/components/InfiniteList';
import { Money } from '../Money';
import { MemberLink } from 'common/components/link/MemberLink';
import { UserPermissions } from 'common/api/models/UserPermissions';
import { toApiError } from 'common/utils/api';
import { ActivityFeedBadgeForm } from './ActivityFeedBadgeForm';
import { Feature } from 'common/features/Feature';
import { canViewBadges } from 'common/features/canViewBadges';

export interface ActivityFeedProps {
  entityId: string;
  entityType: string;
  appState: AppState;
  entityActions: EntityActions;
  onSave?: () => void;
  showTitle?: boolean;
}
export interface ActivityFeedState {
  activity: Activity[];
  next?: string;
  isLoading: boolean;
  filterType: string;
  error?: string;
  isNoteOpen: boolean;
  isCeuOpen: boolean;
  isBadgeOpen: boolean;
}
export class ActivityFeed extends React.Component<ActivityFeedProps, ActivityFeedState> {
  static defaultProps = {
    showTitle: true,
  };

  state: ActivityFeedState = {
    activity: [],
    isNoteOpen: false,
    isCeuOpen: false,
    isBadgeOpen: false,
    isLoading: false,
    filterType: '',
  };

  componentDidMount() {
    this.reload(this.props.entityId);
  }

  componentDidUpdate(props: ActivityFeedProps) {
    if (props.entityId !== this.props.entityId) {
      this.reload(props.entityId);
    }
  }

  handleSave = () => {
    this.setState({ isNoteOpen: false, isCeuOpen: false, isBadgeOpen: false });
    this.reload(this.props.entityId);
    if (this.props.onSave) {
      this.props.onSave();
    }
  };

  handleToggleNote = () => {
    this.setState({ isNoteOpen: !this.state.isNoteOpen });
  };

  handleToggleCeu = () => {
    this.setState({ isCeuOpen: !this.state.isCeuOpen });
  };

  handleToggleBadge = () => {
    this.setState({ isBadgeOpen: !this.state.isBadgeOpen });
  };

  reload(entityId: string, from?: string) {
    this.setState({ isLoading: true });
    ActivityApi.findActivity({
      entityId: entityId,
      from: from,
      type: this.state.filterType,
      subType: this.state.filterType,
    }).then(
      res => {
        this.setState({
          activity: from ? this.state.activity.concat(res.data!) : res.data!,
          next: res.next,
          isLoading: false,
        });
        for (let a of res.data!) {
          this.props.entityActions.fetch(EntityType.MEMBER, a.createId!);
        }
      },
      err => {
        this.setState({ error: toApiError(err), isLoading: false });
      }
    );
  }

  handleLoadMore = () => {
    this.reload(this.props.entityId, this.state.next);
  };

  handleChangeFilter = (e: React.ChangeEvent<HTMLSelectElement>) => {
    this.setState({ filterType: e.currentTarget.value, next: undefined }, () => {
      this.reload(this.props.entityId);
    });
  };

  render() {
    const entities = this.props.appState.entities;
    return (
      <div>
        <Row alignItems="center" justifyContent="space-between" flexWrap="wrap" paddingTop=".5rem">
          <Block margin=".5rem 0">
            {acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ACTIVITY_NOTE) && (
              <button className="btn  btn-secondary" onClick={this.handleToggleNote}>
                <i className="fa fa-sticky-note-o" /> Add Note
              </button>
            )}
            {acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ACTIVITY_POINTS) && (
              <button className="btn  btn-secondary" onClick={this.handleToggleCeu} style={{ marginLeft: 5 }}>
                <i className="fa fa-graduation-cap" /> Add CEU Credit
              </button>
            )}
            {canViewBadges(Feature.badges, this.props.appState.config, this.props.appState.user) &&
              acl.hasPermission(this.props.appState, UserPermissions.Permissions.CREATE_ACTIVITY_POINTS) && (
                <button className="btn  btn-secondary" onClick={this.handleToggleBadge} style={{ marginLeft: 5 }}>
                  <i className="fa fa-certificate" /> Add Badge Event
                </button>
              )}
          </Block>
          <Block alignSelf="flex-end" margin=".5rem 0">
            <label>Filter</label>
            <select
              className="form-control"
              style={{ marginLeft: 10, width: 300, minWidth: 300, display: 'inline-block' }}
              value={this.state.filterType}
              onChange={this.handleChangeFilter}
            >
              <option value="">All activity</option>
              <option value="NOTE">Note</option>
              <option value="DUES">Dues</option>
              <option value="CREATE">Create</option>
              <option value="UPDATE">Update</option>
              <option value="POINTS">CEU Credit</option>
              <option value="MEMBER_APPLICATION">Member Application</option>
              <option value="MEMBER_REINSTATE">Member Reinstated</option>
              <option value="MEMBER_REREGISTER">Member Reregister</option>
              <option value="MEMBER_TERMINATE">Member Terminate</option>
              <option value="ORDER">Order</option>
              <option value="MEETING">Meeting</option>
              <option value="EMAIL">Email</option>
              <option value="LETTER">Letter</option>
              <option value="CALL">Call</option>
              <option value="INFO">Info</option>
              <option value="AWARD">Award</option>
              <option value="REFUND">Refund</option>
              <option value="COUPON">Coupon</option>
              <option value="EXPIRE">Expire</option>
              <option value="CUSTOM">Custom</option>
              <option value="CHAPTER_POSITION_HISTORY">Chapter Position History</option>
              <option value="LEARNING_PROGRAM_BADGE">Badge Learning Program</option>
              <option value="INDIVIDUAL_LEARNING_PROGRAM_BADGE">Individual Badge Learning Program</option>
              <option value="LEARNING_PROGRAM_CERTIFICATION">Certification Learning Program</option>
            </select>
          </Block>
        </Row>
        {this.state.isNoteOpen && (
          <ActivityFeedNoteForm
            entityId={this.props.entityId}
            entityType={this.props.entityType}
            onSave={this.handleSave}
            onCancel={this.handleToggleNote}
          />
        )}
        {this.state.isCeuOpen && (
          <ActivityFeedCeuForm
            entityId={this.props.entityId}
            entityType={this.props.entityType}
            onSave={this.handleSave}
            onCancel={this.handleToggleCeu}
          />
        )}
        {this.state.isBadgeOpen && (
          <ActivityFeedBadgeForm
            entityId={this.props.entityId}
            entityType={this.props.entityType}
            onSave={this.handleSave}
            onCancel={this.handleToggleBadge}
          />
        )}
        {this.state.activity.length === 0 && (
          <div className="row" style={{ marginTop: 10 }}>
            <div className="col">No activity.</div>
          </div>
        )}

        <InfiniteList
          className="feed mb-4"
          hasMore={!!this.state.next}
          isLoading={this.state.isLoading}
          onLoadMore={this.handleLoadMore}
        >
          {this.state.activity.map(a => (
            <div key={a.id} className="card card-responsive mt-2">
              <div className="card-block">
                <h6 className="card-title">
                  <small>
                    <b>
                      <MemberLink member={entities.member[a.createId!]} />
                    </b>{' '}
                    <span className="text-muted">
                      <Datetime at={a.createAt} imprecise={true} />
                    </span>{' '}
                  </small>
                </h6>
                <h6>
                  <b>
                    {a.type === Activity.Type.DUES && (
                      <span>
                        Paid dues: <Money value={a.amount!} /> for <Datetime at={a.startDate} format="MM/DD/YY" />{' '}
                        through <Datetime at={addDays(a.endDate!, -1)} format="MM/DD/YY" />
                      </span>
                    )}
                    {a.type === Activity.Type.CERTIFICATION_ADDED && <span>Certification added</span>}
                    {a.type === Activity.Type.CERTIFICATION_REMOVED && <span>Certification removed</span>}
                    {a.type === Activity.Type.MEMBER_APPLICATION && <span>Applied for membership</span>}
                    {a.type === Activity.Type.MEMBER_REINSTATE && <span>Reinstated membership</span>}
                    {a.type === Activity.Type.MEMBER_REREGISTER && <span>Reregistered membership</span>}
                    {a.type === Activity.Type.MEMBER_TERMINATE && <span>Terminated membership</span>}
                    {a.type === Activity.Type.ORDER && (
                      <span>
                        Ordered: {a.description} x{a.quantity} <Money value={a.amount!} />
                      </span>
                    )}
                    {a.type === Activity.Type.MEETING && <span>Held meeting</span>}
                    {a.type === Activity.Type.EMAIL && <span>Emailed</span>}
                    {a.type === Activity.Type.LETTER && <span>Mailed letter</span>}
                    {a.type === Activity.Type.CALL && <span>Phone call</span>}
                    {canViewBadges(Feature.badges, this.props.appState.config, this.props.appState.user) && (
                      <>
                        {a.type === Activity.Type.BADGE_MEETING && (
                          <span>
                            <i className="fa fa-certificate" /> Badge Event (In-Person)
                          </span>
                        )}
                        {a.type === Activity.Type.BADGE_VIRTUAL && (
                          <span>
                            <i className="fa fa-certificate" /> Badge Event (Virtual)
                          </span>
                        )}
                      </>
                    )}
                    {a.type === Activity.Type.INFO && <span>Info</span>}
                    {a.type === Activity.Type.AWARD && <span>Received award</span>}
                    {a.type === Activity.Type.REFUND && (
                      <span>
                        Issued refund: <Money value={a.amount!} />
                      </span>
                    )}
                    {a.type === Activity.Type.COUPON && <span>Coupon</span>}
                    {a.type === Activity.Type.NOTE && (
                      <span>
                        <i className="fa fa-sticky-note-o" /> Note{' '}
                        {a.subType && (a.subType.toString() === 'NOTE' ? '' : '- ' + a.subType.toString())}
                      </span>
                    )}
                    {a.type === Activity.Type.POINTS && (
                      <span>
                        <i className="fa fa-graduation-cap" /> Earned {Number(a.units).toFixed(2)} CEU credits
                      </span>
                    )}
                    {a.type === Activity.Type.CUSTOM && (
                      <span>
                        {a.customType === 'COMMITTEE' && (
                          <span>
                            Committee assignment <Datetime at={a.startDate} format="MM/DD/YY" /> to{' '}
                            <Datetime at={a.endDate} format="MM/DD/YY" />
                          </span>
                        )}
                        {a.customType !== 'CEU' && a.customType !== 'COMMITTEE' && <span>{a.customType}</span>}
                        {a.customType === 'TRANSCRIPT' && a.units && <span>: earned {a.units} Education Hours</span>}
                        {a.startDate && <Datetime at={a.startDate} format="MM/DD/YY" />}
                        {a.endDate && (
                          <span>
                            {' '}
                            ending <Datetime at={a.endDate} format="MM/DD/YY" />
                          </span>
                        )}
                      </span>
                    )}
                    {a.type === Activity.Type.CHAPTER_POSITION_HISTORY && (
                      <span>
                        <i className="fa fa-sticky-note-o" /> Chapter Position History
                      </span>
                    )}
                    {a.type === Activity.Type.LEARNING_PROGRAM_BADGE && (
                      <span>
                        <i className="fa fa-sticky-note-o" /> Badge Learning Program
                      </span>
                    )}
                    {a.type === Activity.Type.INDIVIDUAL_LEARNING_PROGRAM_BADGE && (
                      <span>
                        <i className="fa fa-sticky-note-o" /> Individual Badge Learning Program
                      </span>
                    )}
                    {a.type === Activity.Type.LEARNING_PROGRAM_CERTIFICATION && (
                      <span>
                        <i className="fa fa-sticky-note-o" /> Certification Learning Program
                      </span>
                    )}
                  </b>
                </h6>
                {a.note && <div>{a.note.split('\n').map((line, i) => <div key={a.id! + i}>{line}</div>)}</div>}
                {a.description && <div>{a.description}</div>}
              </div>
            </div>
          ))}
        </InfiniteList>
      </div>
    );
  }
}



// WEBPACK FOOTER //
// ./src/common/components/activity/ActivityFeed.tsx